import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, MessageCircle, Send, Mail, CheckCircle2 } from "lucide-react";
import { SITE, SIZES } from "@/config/site";
import { Reveal, ChapterTag } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIZE_OPTIONS = [...SIZES.map((s) => s.label), "Aún no lo sé"];
const WHEN_OPTIONS = ["Lo antes posible", "En 1–3 meses", "En 3–6 meses", "Solo estoy mirando"];
const TYPE_OPTIONS = ["Particular", "Autónomo", "Empresa"];
const EMPTY = { name: "", phone: "", email: "", client_type: "", when: "", size: "", contact_pref: "WhatsApp", message: "" };

const inputCls =
  "w-full bg-brand-smoke border-2 border-transparent rounded-2xl px-5 py-3.5 text-base text-brand-ink placeholder:text-brand-ink/40 outline-none focus:border-brand-yellow focus:bg-white transition-[border-color,background-color] duration-200";

export const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onPrefill = (e) =>
      setForm((f) => {
        const d = typeof e.detail === "string" ? { size: e.detail } : e.detail;
        return { ...f, size: d.size, ...(d.unit && !f.message ? { message: `Me interesa el trastero ${d.unit}` } : {}) };
      });
    window.addEventListener("prefill-size", onPrefill);
    return () => window.removeEventListener("prefill-size", onPrefill);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const origen = new URLSearchParams(window.location.search).get("origen");
      await axios.post(`${API}/leads`, { ...form, source: origen || "prerreserva" });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "generate_lead", lead_size: form.size, lead_type: form.client_type, lead_origin: origen || "web" });
      toast.success("¡Prerreserva recibida! Te contactaremos para confirmarte la unidad disponible y tu precio de apertura. Sin pago y sin compromiso.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "No se pudo enviar. Inténtalo de nuevo o escríbenos por WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="py-24 sm:py-32 bg-brand-paper">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <Reveal>
          <ChapterTag num="05" label="Prerreserva" />
          <h2 className="font-display font-extrabold tracking-tight text-brand-ink text-3xl sm:text-4xl lg:text-5xl mt-5 leading-[1.08]">
            Prerreserva tu trastero <span className="bg-brand-yellow px-2 rounded-lg inline-block -rotate-1">sin compromiso</span>.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-brand-ink/60 leading-relaxed max-w-md">
            Haz tu prerreserva antes de la apertura: te confirmaremos la unidad
            disponible y tu precio de apertura, con prioridad para elegir tamaño.
          </p>
          <ul className="mt-8 space-y-3.5">
            {["Prioridad para elegir tamaño", "Descuento en tu primer mes", "Sin compromiso: te contactamos y tú decides"].map((perk) => (
              <li key={perk} className="flex items-center gap-3 text-[15px] font-semibold text-brand-ink/80">
                <CheckCircle2 size={20} className="text-brand-ink fill-brand-yellow shrink-0" />
                {perk}
              </li>
            ))}
          </ul>
          <div className="mt-9 space-y-4">
            <a
              data-testid="contact-phone-link"
              href={SITE.phoneHref}
              className="flex items-center gap-4 group"
            >
              <span className="w-12 h-12 rounded-2xl bg-brand-ink flex items-center justify-center group-hover:bg-brand-yellow transition-colors duration-200">
                <Phone size={20} className="text-brand-yellow group-hover:text-brand-ink transition-colors duration-200" />
              </span>
              <span>
                <span className="block font-mono text-[11px] font-bold tracking-[0.25em] text-brand-ink/45 uppercase">Llámanos</span>
                <span className="font-display font-extrabold text-2xl text-brand-ink">{SITE.phoneDisplay}</span>
              </span>
            </a>
            <a
              data-testid="contact-whatsapp-link"
              href={SITE.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 group"
            >
              <span className="w-12 h-12 rounded-2xl bg-[#25D366] flex items-center justify-center">
                <MessageCircle size={20} className="text-white" />
              </span>
              <span>
                <span className="block font-mono text-[11px] font-bold tracking-[0.25em] text-brand-ink/45 uppercase">WhatsApp</span>
                <span className="font-display font-bold text-lg text-brand-ink">Respuesta en minutos</span>
              </span>
            </a>
            <a
              data-testid="contact-email-link"
              href={SITE.emailHref}
              className="flex items-center gap-4 group"
            >
              <span className="w-12 h-12 rounded-2xl bg-brand-yellow flex items-center justify-center">
                <Mail size={20} className="text-brand-ink" />
              </span>
              <span>
                <span className="block font-mono text-[11px] font-bold tracking-[0.25em] text-brand-ink/45 uppercase">Email</span>
                <span className="font-display font-bold text-base sm:text-lg text-brand-ink break-all">{SITE.emailDisplay}</span>
              </span>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            data-testid="contact-form"
            id="formulario"
            onSubmit={submit}
            className="bg-white rounded-3xl border border-brand-ink/8 shadow-[0_20px_60px_rgba(18,19,22,0.08)] p-7 sm:p-9 space-y-5"
          >
            <div>
              <label htmlFor="cf-name" className="block font-display font-bold text-sm text-brand-ink mb-2">Nombre</label>
              <input id="cf-name" data-testid="contact-form-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Tu nombre" className={inputCls} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="cf-phone" className="block font-display font-bold text-sm text-brand-ink mb-2">Teléfono</label>
                <input id="cf-phone" data-testid="contact-form-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="600 000 000" className={inputCls} />
              </div>
              <div>
                <label htmlFor="cf-email" className="block font-display font-bold text-sm text-brand-ink mb-2">Email <span className="font-sans font-normal text-brand-ink/40">(opcional)</span></label>
                <input id="cf-email" data-testid="contact-form-email-input" type="email" value={form.email} onChange={set("email")} placeholder="tu@email.com" className={inputCls} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="cf-type" className="block font-display font-bold text-sm text-brand-ink mb-2">¿Para quién es? <span className="font-sans font-normal text-brand-ink/40">(opcional)</span></label>
                <select id="cf-type" data-testid="contact-form-type-select" value={form.client_type} onChange={set("client_type")} className={`${inputCls} appearance-none cursor-pointer ${form.client_type ? "" : "text-brand-ink/40"}`}>
                  <option value="" disabled>Particular o empresa</option>
                  {TYPE_OPTIONS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="cf-when" className="block font-display font-bold text-sm text-brand-ink mb-2">¿Cuándo lo necesitas?</label>
                <select id="cf-when" data-testid="contact-form-when-select" required value={form.when} onChange={set("when")} className={`${inputCls} appearance-none cursor-pointer ${form.when ? "" : "text-brand-ink/40"}`}>
                  <option value="" disabled>Elige una opción</option>
                  {WHEN_OPTIONS.map((w) => (
                    <option key={w} value={w}>{w}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="cf-size" className="block font-display font-bold text-sm text-brand-ink mb-2">Tamaño aproximado que necesitas</label>
              <select id="cf-size" data-testid="contact-form-size-select" required value={form.size} onChange={set("size")} className={`${inputCls} appearance-none cursor-pointer ${form.size ? "" : "text-brand-ink/40"}`}>
                <option value="" disabled>Selecciona un tamaño</option>
                {SIZE_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <span className="block font-display font-bold text-sm text-brand-ink mb-2">¿Cómo prefieres que te contactemos?</span>
              <div className="grid grid-cols-2 gap-3">
                {["WhatsApp", "Llamada"].map((pref) => (
                  <button
                    key={pref}
                    type="button"
                    data-testid={`contact-pref-${pref.toLowerCase()}`}
                    onClick={() => setForm({ ...form, contact_pref: pref })}
                    className={`font-display font-bold text-sm tracking-wide py-3.5 rounded-2xl border-2 transition-[background-color,border-color,color] duration-200 ${
                      form.contact_pref === pref
                        ? "bg-brand-ink text-brand-yellow border-brand-ink"
                        : "bg-brand-smoke text-brand-ink/60 border-transparent hover:border-brand-ink/30"
                    }`}
                  >
                    {pref === "WhatsApp" ? "Por WhatsApp" : "Con una llamada"}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="cf-message" className="block font-display font-bold text-sm text-brand-ink mb-2">Mensaje <span className="font-sans font-normal text-brand-ink/40">(opcional)</span></label>
              <textarea id="cf-message" data-testid="contact-form-message-input" rows={3} value={form.message} onChange={set("message")} placeholder="Cuéntanos qué quieres guardar" className={`${inputCls} resize-none`} />
            </div>
            <motion.button
              data-testid="contact-form-submit-button"
              type="submit"
              disabled={sending}
              whileHover={{ scale: sending ? 1 : 1.02 }}
              whileTap={{ scale: sending ? 1 : 0.98 }}
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-yellow text-brand-ink font-display font-extrabold tracking-wide px-8 py-4 rounded-full shadow-[0_10px_30px_rgba(255,230,0,0.35)] hover:bg-brand-yellowhover transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Send size={17} strokeWidth={2.4} />
              {sending ? "ENVIANDO..." : "PRERRESERVAR MI TRASTERO"}
            </motion.button>
            <p className="text-xs text-brand-ink/40 text-center">
              Al enviar aceptas nuestra <a href="/legal/politica-de-privacidad" className="underline">política de privacidad</a>. Sin compromiso.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
